import React from 'react';
import { cn } from '@/lib/cn';
import { BottomSheet } from '@/components/ui';
import { ListCell } from '@/components/ui/ListCell';
import { useStore } from '@/providers/StoreProvider';
import { FilterActionBar } from './FilterActionBar';
import { CheckboxIcon } from '@/components/ui';
import { IconRentStabilized, IconVerified, IconGoodCause, IconTopRated } from '@repo/svgs';
import { useAnalytics } from '@/hooks/useAnalytics';
import { RentStabilizedDownloadModal } from '@/components/modal/RentStabilizedDownloadModal';

export interface BuildingStatusFilterProps {
  filterType: 'isRentStabilized' | 'isVerified' | 'hasBuildingGoodCauseEviction' | 'isTopRated';
  variant?: 'desktop' | 'mobile';
  className?: string;
  // Mobile-specific props
  open?: boolean;
  onClose?: () => void;
  requireConfirmation?: boolean;
}

type FilterConfig = {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  description: string;
  title: string;
};

const FILTER_CONFIG: Record<BuildingStatusFilterProps['filterType'], FilterConfig> = {
  isRentStabilized: {
    icon: IconRentStabilized,
    label: 'Rent Stabilized',
    description: 'Apartments that entitle you to a renewal and limited rent increases.',
    title: 'Rent-stabilized apartments',
  },
  isVerified: {
    icon: IconVerified,
    label: 'Verified',
    description: 'Apartments with confirmed availability which you can apply to directly.',
    title: 'Verified apartment listings',
  },
  hasBuildingGoodCauseEviction: {
    icon: IconGoodCause,
    label: 'Good Cause',
    description:
      'Buildings that guarantee a renewal and capped rent increases, if you follow your lease terms.',
    title: 'Apartments in good cause buildings',
  },
  isTopRated: {
    icon: IconTopRated,
    label: 'Top Rated',
    description:
      'Buildings that are highly reviewed and rated 4+ stars by past and current renters.',
    title: 'Apartments in top rated buildings',
  },
};

// Shared hook for building status filter logic - exported for reuse
export function useBuildingStatusFilter(filterType: BuildingStatusFilterProps['filterType']) {
  const analytics = useAnalytics();
  const buildingStatus = useStore(s => s.listingsFilters.buildingStatus);
  const toggleBuildingStatusFilter = useStore(s => s.listingsFilters.toggleBuildingStatusFilter);
  const resetBuildingStatusFilter = useStore(s => s.listingsFilters.resetBuildingStatusFilter);

  const config = FILTER_CONFIG[filterType];
  const isSelected = buildingStatus[filterType] === true;
  const filterValue = buildingStatus[filterType];

  const handleToggle = () => {
    const eventName =
      filterType === 'isVerified'
        ? 'listings_filter_verified'
        : filterType === 'isTopRated'
          ? 'listings_filter_toprated'
          : filterType === 'isRentStabilized'
            ? 'listings_filter_rent_stabilized'
            : 'listings_filter_goodcause';

    const nextVal = isSelected ? null : true;
    analytics.track(eventName, { val: nextVal });
    toggleBuildingStatusFilter(filterType);
  };

  const handleReset = () => {
    resetBuildingStatusFilter(filterType);
  };

  const formatFilterDisplay = () => {
    if (filterValue === null) {
      return undefined; // Show placeholder
    }
    return config.label;
  };

  return {
    config,
    isSelected,
    filterValue,
    handleToggle,
    handleReset,
    formatFilterDisplay,
  };
}

export function BuildingStatusFilters({
  filterType,
  variant = 'desktop',
  className,
  open,
  onClose,
  requireConfirmation = false,
}: BuildingStatusFilterProps) {
  const { config, isSelected, handleToggle, handleReset } = useBuildingStatusFilter(filterType);
  const IconComponent = config?.icon;

  // Rent-stabilized filter is a web-side "dead end" — the full
  // experience lives in the app. Clicking the row anywhere it appears
  // (chip flow already intercepts at FiltersBar; this covers the "All
  // filters" panel + any other consumer of BuildingStatusFilters)
  // opens the download-prompt modal instead of toggling the checkbox.
  // The checkbox never flips true because we short-circuit before
  // calling handleToggle.
  const isRentStab = filterType === 'isRentStabilized';
  const [rentStabModalOpen, setRentStabModalOpen] = React.useState(false);
  const handleRowClick = isRentStab ? () => setRentStabModalOpen(true) : handleToggle;

  if (variant === 'mobile') {
    return (
      <>
        <BottomSheet
          label={config.title}
          bodyClassName="py-5 px-0"
          open={open!}
          onClose={onClose!}
          leading={false}
        >
          {/* Selection Container */}
          <div className="py-4">
            <div className="flex items-start py-0">
              <ListCell
                leadingIcon={<IconComponent className="w-6 h-6" />}
                title={config.description}
                trailing={<CheckboxIcon checked={isSelected} />}
                onClick={handleRowClick}
                className="flex-1"
              />
            </div>
          </div>

          {/* Action Bar */}
          <FilterActionBar
            scope={{ kind: 'buildingStatus', key: filterType }}
            className=" py-4"
            onApply={() => onClose?.()}
            onReset={handleReset}
          />
        </BottomSheet>
        {isRentStab && (
          <RentStabilizedDownloadModal
            open={rentStabModalOpen}
            onClose={() => setRentStabModalOpen(false)}
            source="listings_all_filters"
          />
        )}
      </>
    );
  }

  return (
    <div className={cn(className)}>
      {/* Selection Container */}
      <ListCell
        leadingIcon={<IconComponent className="w-6 h-6" />}
        title={config.title}
        subtitle={config.description}
        trailing={<CheckboxIcon checked={isSelected} />}
        onClick={handleRowClick}
        className="flex-1 max-w-sm"
      />

      {requireConfirmation && (
        <FilterActionBar
          scope={{ kind: 'buildingStatus', key: filterType }}
          className="mt-6"
          onReset={handleReset}
        />
      )}

      {isRentStab && (
        <RentStabilizedDownloadModal
          open={rentStabModalOpen}
          onClose={() => setRentStabModalOpen(false)}
          source="listings_all_filters"
          nested
        />
      )}
    </div>
  );
}

export default BuildingStatusFilters;
